import classNames from 'classnames';
import * as React from 'react';
import { ReactNode, useState } from 'react';

import CloseButton from '../CloseButton/CloseButton';
import SVG from '../SVG/SVG';
import Title from '../Title/Title';

import mutedIcon from './graphics/dimmed.svg?raw';
import errorIcon from './graphics/error.svg?raw';
import infoIcon from './graphics/info.svg?raw';
import successIcon from './graphics/success.svg?raw';
import warningIcon from './graphics/warning.svg?raw';

import styles from './Banner.module.css';

const dynamicStyles: Record<string, string> = styles;

export type Level = 'muted' | 'info' | 'success' | 'warning' | 'error';

const levelToIcon = (level: Level) => {
    switch (level) {
        case 'muted':
            return mutedIcon;
        case 'info':
            return infoIcon;
        case 'success':
            return successIcon;
        case 'warning':
            return warningIcon;
        case 'error':
            return errorIcon;
    }

    return errorIcon;
};

type BannerProps<T extends boolean> = {
    level: Level;
    withoutBackground?: boolean;
    dismissible?: T;
    onDismissed?: T extends true ? () => void : never;
    children: ReactNode;
    title?: React.ReactChild;
};

const Banner = <T extends boolean>({
    level,
    title,
    children,
    dismissible,
    onDismissed,
    withoutBackground = false,
}: BannerProps<T>) => {
    const [dismissed, setDismissed] = useState(false);
    if (dismissed) {
        return null;
    }

    const classes = classNames(styles.Root, dynamicStyles[`level-${level}`], {
        [styles.muted]: level === 'muted',
        [styles.info]: level === 'info',
        [styles.success]: level === 'success',
        [styles.warning]: level === 'warning',
        [styles.error]: level === 'error',
        [styles.withoutBackground]: withoutBackground,
        [styles.dismissible]: dismissible,
    });

    const iconClasses = classNames(styles.Icon, {
        [styles.Icon_muted]: level === 'muted',
        [styles.Icon_info]: level === 'info',
        [styles.Icon_success]: level === 'success',
        [styles.Icon_warning]: level === 'warning',
        [styles.Icon_error]: level === 'error',
    });

    const icon = levelToIcon(level);
    const dismiss = () => {
        onDismissed && onDismissed();
        setDismissed(true);
    };

    return (
        <div className={classes}>
            {icon && <SVG src={icon} className={iconClasses} />}

            <div className={styles.Content}>
                {title ? <Title>{title}</Title> : null}
                {children}
            </div>

            {dismissible && (
                <CloseButton
                    className={styles.Close}
                    onClick={dismiss}
                    variant="clear"
                />
            )}
        </div>
    );
};

export default Banner;
